import React from 'react';
import { Link } from 'react-router-dom';
import { QrCode, Eye, Users, UserCheck, Copy, ArrowRight, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';

import PortalLayout from '../PortalLayout';
import { usePortal } from '../../../contexts/PortalContext';
import { usePortalSummary, usePortalReferrals, usePortalMe } from '../../../services/portal';
import { TILE, TILE_PAD, StatTile, StageBadge, SkeletonRow, ErrorState, money, fmtDate } from '../portalShared';

// Portal Home — the "not a ghost program" answer at a glance: sticky money
// summary, funnel stat tiles, the partner's link, and their latest referrals.
const PortalHome = () => {
  const { portalUser, updatePortalStatus } = usePortal();
  const me = usePortalMe();
  const summary = usePortalSummary();
  const referrals = usePortalReferrals(1);

  // Keep the cached status bucket in sync (e.g. deactivated mid-session).
  React.useEffect(() => {
    if (me.data?.status) updatePortalStatus(me.data.status);
  }, [me.data?.status, updatePortalStatus]);

  const s = summary.data;
  const loading = summary.isLoading;
  const firstName = me.data?.firstName || portalUser?.displayName?.split(' ')[0] || 'there';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(s?.link?.url || '');
      toast.success('Link copied!');
    } catch (e) {
      toast.error('Could not copy — please copy it manually.');
    }
  };

  const recent = (referrals.data?.rows || []).slice(0, 3);

  const summaryFailed = summary.isError;

  return (
    <PortalLayout>
      {/* Sticky-feel summary header: greeting + owed pill. */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-white">Welcome back, {firstName}</h1>
          <p className="mt-0.5 text-sm text-neutral-400">Here&apos;s how your referrals are doing.</p>
        </div>
        {loading ? (
          <div className="h-9 w-28 animate-pulse rounded-full bg-white/10" />
        ) : summaryFailed ? null : (
          <Link
            to="/portal/payouts"
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-emerald-500/15 px-4 text-sm font-semibold text-emerald-300 transition-colors hover:bg-emerald-500/25"
          >
            {s?.totals?.owed > 0 ? `${money(s.totals.owed)} owed to you` : `${money(s?.totals?.paid)} earned`}
            <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>

      {s?.partnerActive === false ? (
        <div className="mb-5 flex items-start gap-2 rounded-xl bg-amber-500/15 px-4 py-3 text-sm text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Your referral link is currently inactive — reach out to us if you think that&apos;s a
          mistake. Your history and payouts below are unaffected.
        </div>
      ) : null}

      {summaryFailed ? (
        <ErrorState onRetry={summary.refetch} />
      ) : null}

      <div className={summaryFailed ? 'hidden' : 'grid grid-cols-2 gap-3 md:grid-cols-4'}>
        <StatTile icon={QrCode} label="QR scans" value={s?.stats?.scans ?? 0} loading={loading} />
        <StatTile icon={Eye} label="Link visits" value={s?.stats?.visits ?? 0} loading={loading} />
        {/* The referrals list total (form + link rows) is the truer "sent"
            figure than the lead count — form leads live under the umbrella
            until approval back-credits them. */}
        <StatTile
          icon={Users}
          label="Referrals"
          value={referrals.data?.total ?? s?.stats?.leads ?? 0}
          loading={referrals.isLoading}
        />
        <StatTile icon={UserCheck} label="Clients" value={s?.stats?.clients ?? 0} loading={loading} />
      </div>

      {/* Link card */}
      <div className={`${TILE_PAD} mt-4 ${summaryFailed ? "hidden" : ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wide text-neutral-400">
              Your referral link
            </div>
            {loading ? (
              <div className="mt-2 h-4 w-56 animate-pulse rounded-md bg-white/10" />
            ) : (
              <div className="mt-1 truncate text-sm text-white">{s?.link?.url || '—'}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyLink}
              disabled={loading || !s?.link?.url}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md bg-white/10 px-4 text-sm font-medium text-white transition-colors hover:bg-white/15 disabled:opacity-40"
            >
              <Copy className="h-4 w-4" /> Copy
            </button>
            <Link
              to="/portal/link"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-md bg-black px-4 text-sm font-semibold text-white transition-colors hover:!bg-white hover:!text-black"
            >
              <QrCode className="h-4 w-4" /> QR code
            </Link>
          </div>
        </div>
      </div>

      {/* Recent referrals */}
      <div className="mb-2 mt-6 flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-neutral-300">
          Recent referrals
        </h2>
        <Link
          to="/portal/referrals"
          className="inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-neutral-400 transition-colors hover:text-white"
        >
          View all <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <div className="space-y-2.5">
        {referrals.isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : referrals.isError ? (
          <ErrorState onRetry={referrals.refetch} />
        ) : recent.length === 0 ? (
          <div className={`${TILE} px-5 py-8 text-center text-sm text-neutral-400`}>
            No referrals yet — share your link or QR code to get started.
          </div>
        ) : (
          recent.map((r) => (
            <div key={r.id} className={`${TILE} flex items-center justify-between px-4 py-3`}>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-white">
                  {r.type === 'form' ? r.name : r.label}
                </div>
                <div className="mt-0.5 text-xs text-neutral-400">{fmtDate(r.occurredAt)}</div>
              </div>
              <StageBadge stage={r.stage} />
            </div>
          ))
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalHome;
